import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from "react";
import { socket } from "../utils/socket";
import { Notification } from "../interfaces";
import { debounce } from "lodash";
import { getNotificationsByUser } from "../features/slices/Notification";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../store";
import { useSnackbar } from "./SnackbarContext";
import { Link } from "@mui/material";

interface MessagesContextProps {
  notifications: Notification[];
  markAsRead: (id: number) => void;
  markAllAsRead: () => void;
  removeNotification: (id: number) => void;
}

const MessagesContext = createContext<MessagesContextProps | undefined>(
  undefined
);

export const MessagesProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const dispatch = useDispatch<AppDispatch>();
  const { showSnackbar } = useSnackbar();
  const { user, isInit } = useSelector((state: RootState) => state.auth);
  const fetchNotifications = useCallback(
    debounce(async () => {
      if (!user || isInit) return;
      try {
        const response = await dispatch(getNotificationsByUser(user.id));
        setNotifications(response.payload);
      } catch (error) {
        console.error("Error fetching notifications:", error);
      }
    }, 300),
    [user, dispatch]
  );

  useEffect(() => {
    if (user) {
      fetchNotifications();
    }
    socket.on("new_notification", (notification: Notification) => {
      setNotifications((prev) => [...prev, notification]);

      const message = (
        <span>
          {notification.message}
          {notification.attachment_url && (
            <Link
              href={notification.attachment_url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: "block",
                mt: 1,
                color: "#FFFFFF",
                fontSize: "0.875rem",
                fontWeight: "bold",
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              View Here
            </Link>
          )}
        </span>
      );

      if (notification.user_id === user.id) {
        showSnackbar(message, notification.category || "info");
      }
    });

    socket.on("notification_updated", (updatedNotification: Notification) => {
      setNotifications((prev) =>
        prev.map((notif) =>
          notif.id === updatedNotification.id ? updatedNotification : notif
        )
      );
      if (
        updatedNotification.type !== "progress" &&
        updatedNotification.message
      ) {
        const message = (
          <span>
            {updatedNotification.message}
            {updatedNotification.attachment_url && (
              <Link
                href={updatedNotification.attachment_url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: "block",
                  mt: 1,
                  color: "#FFFFFF",
                  fontSize: "0.875rem",
                  fontWeight: "bold",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                View Here
              </Link>
            )}
          </span>
        );
        if (updatedNotification.user_id === user.id) {
          showSnackbar(message, updatedNotification.category || "info");
        }
      }
      if (
        updatedNotification.type === "progress" &&
        updatedNotification.progress === 100 &&
        updatedNotification.category === "success"
      ) {
        const completionMessage = (
          <span>
            {updatedNotification.message}
            {updatedNotification.attachment_url && (
              <Link
                href={updatedNotification.attachment_url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: "block",
                  mt: 1,
                  color: "#FFFFFF",
                  fontSize: "0.875rem",
                  fontWeight: "bold",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                View Here
              </Link>
            )}
          </span>
        );
        if (updatedNotification.user_id === user.id) {
          showSnackbar(completionMessage, "success");
        }
      }
    });

    return () => {
      socket.off("new_notification");
      socket.off("notification_updated");
    };
  }, [showSnackbar, user, fetchNotifications]);

  const markAsRead = (id: number) => {
    socket.emit("mark_as_read", { notification_id: id });

    setNotifications((prev) =>
      prev.map((notif) =>
        notif.id === id ? { ...notif, is_read: true } : notif
      )
    );
  };

  const markAllAsRead = () => {
    socket.emit("mark_all_as_read", { user_id: user.id });

    setNotifications((prev) =>
      prev.map((notif) => ({ ...notif, is_read: true }))
    );
  };

  const removeNotification = (id: number) => {
    setNotifications((prev) => prev.filter((notif) => notif.id !== id));
  };

  return (
    <MessagesContext.Provider
      value={{
        notifications,
        markAsRead,
        markAllAsRead,
        removeNotification,
      }}
    >
      {children}
    </MessagesContext.Provider>
  );
};

export const useMessages = () => {
  const context = useContext(MessagesContext);
  if (!context) {
    throw new Error("useMessages must be used within a MessagesProvider");
  }
  return context;
};
